/**
 * Admin · MCP Facebook.  (spec 039)
 *
 * Cliente del MCP remoto de Meta. No es el MCP de Cursor.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router'

import { useSesion } from '../../auth'
import { api, ErrorApi } from '../../lib/api'
import { fechaHoraLarga } from '../../lib/tiempo'
import {
    agruparHerramientas,
    camposDeSchema,
    destinoDeEnvio,
    DOC_MCP,
    esSystemUserToken,
    exigeConfirmarEnvio,
    TOOLS_DISCOVERY,
    type HerramientaMcp,
} from '../../lib/mcp-facebook'

interface SesionMeta {
    conectado: boolean
    caducaEn: string | null
}

interface NegocioAgendify {
    accountId: number
    name: string
    estadoAlta: string
    altaId: number | null
}

interface CuentaWhatsappMeta {
    id: string
    nombre: string
    appId: string | null
}

interface NumeroWhatsappMeta {
    id: string
    numero: string
    estado: string | null
    cuentaId: string | null
}

interface NegocioMeta {
    id: string
    nombre: string
    tieneWhatsapp: boolean
    cuentas: CuentaWhatsappMeta[]
    numeros: NumeroWhatsappMeta[]
    aviso: string | null
}

interface Descubrimiento {
    pedido: boolean
    refrescadoEn: string | null
    negocios: NegocioMeta[]
    fuente: 'mcp' | 'graph' | null
    mcp: { listo: boolean; detalle: string | null }
}

function resultadoTexto(r: unknown): string {
    try {
        return JSON.stringify(r, null, 2)
    } catch {
        return String(r)
    }
}

function FormHerramienta({
    tool,
    onResultado,
}: {
    tool: HerramientaMcp
    onResultado: (t: string) => void
}) {
    const campos = camposDeSchema(tool.inputSchema)
    const [valores, setValores] = useState<Record<string, string>>({})
    const [jsonLibre, setJsonLibre] = useState('{}')
    const [confirmo, setConfirmo] = useState(false)
    const [aviso, setAviso] = useState<string | null>(null)
    const enviar = exigeConfirmarEnvio(tool.name)

    const llamar = useMutation({
        mutationFn: async () => {
            const args: Record<string, unknown> =
                campos.length > 0
                    ? Object.fromEntries(
                          Object.entries(valores)
                              .filter(([, v]) => v.trim() !== '')
                              .map(([k, v]) => [k, v]),
                      )
                    : (JSON.parse(jsonLibre || '{}') as Record<string, unknown>)
            if (enviar && !confirmo) {
                throw new Error('confirma el destino antes de enviar')
            }
            return api.post<{ result: unknown }>('/mcp-facebook/herramientas/llamar', {
                name: tool.name,
                arguments: args,
                ...(enviar ? { confirmarDestino: true } : {}),
            })
        },
        onSuccess: (r) => {
            setAviso(null)
            onResultado(resultadoTexto(r.result))
        },
        onError: (err: Error) => setAviso(err.message),
    })

    const destino = destinoDeEnvio(
        campos.length > 0
            ? valores
            : (() => {
                  try {
                      return JSON.parse(jsonLibre || '{}') as Record<string, unknown>
                  } catch {
                      return {}
                  }
              })(),
    )

    return (
        <form
            className="space-y-2 rounded-md border border-neutral-800 p-3"
            onSubmit={(e) => {
                e.preventDefault()
                llamar.mutate()
            }}
        >
            <div>
                <h4 className="font-mono text-sm text-neutral-100">{tool.name}</h4>
                {tool.description && (
                    <p className="mt-0.5 text-xs text-neutral-500">{tool.description}</p>
                )}
            </div>
            {esSystemUserToken(tool.name) && (
                <p className="text-xs text-amber-200/90">
                    El MCP enseña el enlace a System Users. El token no viaja en claro.
                </p>
            )}
            {campos.length > 0 ? (
                <div className="grid gap-2">
                    {campos.map((c) => (
                        <label key={c.name} className="block text-xs text-neutral-400">
                            {c.name}
                            {c.required ? ' *' : ''}
                            {c.description ? ` — ${c.description}` : ''}
                            <input
                                className="mt-1 w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1 text-sm text-neutral-100"
                                value={valores[c.name] ?? ''}
                                onChange={(e) =>
                                    setValores((v) => ({ ...v, [c.name]: e.target.value }))
                                }
                                required={c.required}
                            />
                        </label>
                    ))}
                </div>
            ) : (
                <label className="block text-xs text-neutral-400">
                    Argumentos (JSON)
                    <textarea
                        className="mt-1 w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1 font-mono text-xs text-neutral-100"
                        rows={4}
                        value={jsonLibre}
                        onChange={(e) => setJsonLibre(e.target.value)}
                    />
                </label>
            )}
            {enviar && (
                <label className="flex items-start gap-2 text-sm text-amber-100">
                    <input
                        type="checkbox"
                        checked={confirmo}
                        onChange={(e) => setConfirmo(e.target.checked)}
                    />
                    <span>
                        Confirmo el destino
                        {destino ? ` (${destino})` : ''} antes de enviar. Meta lo exige; no se manda
                        a un número real «para probar».
                    </span>
                </label>
            )}
            {aviso && <p className="text-xs text-rose-400">{aviso}</p>}
            <button
                type="submit"
                disabled={llamar.isPending || (enviar && !confirmo)}
                className="rounded-md bg-sky-600 px-3 py-1.5 text-sm text-white disabled:opacity-40"
            >
                {llamar.isPending ? 'Llamando…' : 'Ejecutar'}
            </button>
        </form>
    )
}

export function McpFacebook() {
    const { data: sesion } = useSesion()
    const qc = useQueryClient()
    const [resultado, setResultado] = useState<string | null>(null)
    const [pedirHerramientas, setPedirHerramientas] = useState(false)
    const [avisoConectar, setAvisoConectar] = useState<string | null>(null)
    const [conectar, setConectar] = useState({
        accountId: '',
        phoneNumber: '',
        phoneNumberId: '',
        businessAccountId: '',
        apiKey: '',
    })

    const sesionMeta = useQuery({
        queryKey: ['mcp-facebook', 'sesion'],
        queryFn: () => api.get<SesionMeta>('/mcp-facebook/sesion'),
        enabled: sesion?.role === 'admin',
    })

    const listado = useQuery({
        queryKey: ['mcp-facebook', 'descubrimiento'],
        queryFn: () => api.get<Descubrimiento>('/mcp-facebook/descubrimiento'),
        enabled: sesion?.role === 'admin' && sesionMeta.data?.conectado === true,
        retry: false,
    })

    const tools = useQuery({
        queryKey: ['mcp-facebook', 'herramientas'],
        queryFn: () => api.get<{ tools: HerramientaMcp[] }>('/mcp-facebook/herramientas'),
        enabled:
            sesion?.role === 'admin' &&
            sesionMeta.data?.conectado === true &&
            pedirHerramientas &&
            listado.data?.mcp.listo === true,
        retry: false,
    })

    const negocios = useQuery({
        queryKey: ['mcp-facebook', 'negocios-agendify'],
        queryFn: () => api.get<{ items: NegocioAgendify[] }>('/mcp-facebook/negocios-agendify'),
        enabled: sesion?.role === 'admin',
    })

    const grupos = useMemo(
        () => agruparHerramientas(tools.data?.tools ?? []),
        [tools.data?.tools],
    )

    const refrescar = useMutation({
        mutationFn: () => api.post<Descubrimiento>('/mcp-facebook/descubrimiento'),
        onSuccess: (data) => {
            qc.setQueryData(['mcp-facebook', 'descubrimiento'], data)
            if (data.mcp.listo) {
                setPedirHerramientas(true)
                void qc.invalidateQueries({ queryKey: ['mcp-facebook', 'herramientas'] })
            } else {
                setPedirHerramientas(false)
            }
        },
    })

    const empezar = useMutation({
        mutationFn: () =>
            api.post<{ url: string }>('/mcp-facebook/oauth/empezar', {
                redirectUri: `${window.location.origin}/mcp-facebook/oauth`,
            }),
        onSuccess: (r) => {
            window.location.href = r.url
        },
    })

    const salir = useMutation({
        mutationFn: () => api.post('/mcp-facebook/oauth/salir'),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['mcp-facebook'] })
            setResultado(null)
        },
    })

    const atar = useMutation({
        mutationFn: () =>
            api.post<{ ok: true; modo: string; inboxId?: number }>('/mcp-facebook/conectar', {
                accountId: Number(conectar.accountId),
                phoneNumber: conectar.phoneNumber,
                phoneNumberId: conectar.phoneNumberId,
                businessAccountId: conectar.businessAccountId,
                apiKey: conectar.apiKey,
            }),
        onSuccess: (r) => {
            setAvisoConectar(
                r.modo === 'reanudado'
                    ? 'Credenciales aportadas. El aprovisionador reanuda el tramo WhatsApp.'
                    : `Inbox WhatsApp creado${r.inboxId ? ` (#${r.inboxId})` : ''}.`,
            )
            void qc.invalidateQueries({ queryKey: ['mcp-facebook', 'negocios-agendify'] })
            void qc.invalidateQueries({ queryKey: ['altas'] })
        },
        onError: (err: Error) => setAvisoConectar(err.message),
    })

    if (sesion && sesion.role !== 'admin') {
        return <p className="text-sm text-neutral-500">No encontrado</p>
    }

    const conectado = sesionMeta.data?.conectado === true
    const sinSesionMeta = sesionMeta.data && !sesionMeta.data.conectado

    return (
        <div className="mx-auto max-w-3xl space-y-8" data-testid="mcp-facebook">
            <header className="space-y-2">
                <Link to="/" className="text-xs text-neutral-500 hover:text-neutral-300">
                    ← Operación
                </Link>
                <h1 className="text-lg font-semibold text-neutral-100">MCP Facebook</h1>
                <p className="text-sm text-neutral-400">
                    Entras con tu cuenta de desarrollador. No se registra ningún cliente. El{' '}
                    <a
                        href={DOC_MCP}
                        className="text-sky-400 hover:underline"
                        target="_blank"
                        rel="noreferrer"
                    >
                        MCP de Meta
                    </a>{' '}
                    lista los negocios (Business) que te conceda Facebook y, en cada uno, si hay
                    WhatsApp, el número y los ids. No es el listado de apps de
                    developers.facebook.com ni el MCP de Cursor.
                </p>
            </header>

            <section className="space-y-3 rounded-lg border border-neutral-800 p-4" aria-label="Sesión Meta">
                <h2 className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                    Sesión con Meta
                </h2>
                {sesionMeta.isPending && <p className="text-sm text-neutral-500">Comprobando…</p>}
                {sesionMeta.isError && (
                    <p className="text-sm text-rose-400">
                        {sesionMeta.error instanceof ErrorApi
                            ? sesionMeta.error.message
                            : 'No se pudo leer la sesión'}
                    </p>
                )}
                {sinSesionMeta && (
                    <p className="text-sm text-amber-200/90" data-testid="mcp-sin-sesion">
                        No hay sesión con Meta. El botón abre Facebook con tu cuenta; no da de alta
                        clientes. Sin entrar, no se finge una lista vacía.
                    </p>
                )}
                {conectado && (
                    <p className="text-sm text-emerald-300/90">
                        Conectado
                        {sesionMeta.data?.caducaEn
                            ? ` · caduca el ${fechaHoraLarga(sesionMeta.data.caducaEn)}`
                            : ''}
                    </p>
                )}
                <div className="flex gap-2">
                    {!conectado && (
                        <button
                            type="button"
                            className="rounded-md bg-sky-600 px-3 py-1.5 text-sm text-white"
                            onClick={() => empezar.mutate()}
                            disabled={empezar.isPending}
                        >
                            Entrar con Meta
                        </button>
                    )}
                    {conectado && (
                        <button
                            type="button"
                            className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm text-neutral-200"
                            onClick={() => salir.mutate()}
                            disabled={salir.isPending}
                        >
                            Salir de Meta
                        </button>
                    )}
                </div>
                {empezar.isError && (
                    <p className="text-xs text-rose-400">
                        {empezar.error instanceof Error ? empezar.error.message : 'OAuth no arrancó'}
                    </p>
                )}
            </section>

            {conectado && (
                <>
                    <section
                        className="space-y-3 rounded-lg border border-neutral-800 p-4"
                        aria-label="Negocios de Meta"
                        data-testid="mcp-listado"
                    >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <h2 className="text-sm font-semibold text-neutral-200">
                                Negocios y WhatsApp
                            </h2>
                            <button
                                type="button"
                                className="rounded-md border border-neutral-700 px-2 py-1 text-xs text-neutral-200"
                                onClick={() => refrescar.mutate()}
                                disabled={refrescar.isPending}
                            >
                                {refrescar.isPending
                                    ? 'Actualizando…'
                                    : 'Actualizar desde Facebook'}
                            </button>
                        </div>
                        {listado.data && !listado.data.pedido && (
                            <p className="text-xs text-neutral-400" data-testid="mcp-comprobador">
                                No se ha pedido a Facebook. Abrir esta pantalla no consulta la API.
                            </p>
                        )}
                        {listado.data?.pedido && (
                            <p
                                className={
                                    listado.data.mcp.listo
                                        ? 'text-xs text-emerald-300/90'
                                        : 'text-xs text-amber-200/90'
                                }
                                data-testid="mcp-comprobador"
                            >
                                {listado.data.mcp.listo
                                    ? 'MCP listo. El listado y las herramientas vienen de Meta.'
                                    : `MCP no listo. Listado temporal por Graph${
                                          listado.data.mcp.detalle
                                              ? ` (${listado.data.mcp.detalle})`
                                              : ''
                                      }.`}
                                {listado.data.refrescadoEn
                                    ? ` Pedido el ${fechaHoraLarga(listado.data.refrescadoEn)}.`
                                    : ''}
                            </p>
                        )}
                        {listado.isPending && (
                            <p className="text-sm text-neutral-500">Leyendo la última copia…</p>
                        )}
                        {refrescar.isError && (
                            <p className="text-sm text-rose-400">
                                {refrescar.error instanceof ErrorApi
                                    ? refrescar.error.message
                                    : refrescar.error instanceof Error
                                      ? refrescar.error.message
                                      : 'No se pudo actualizar desde Facebook'}
                            </p>
                        )}
                        {listado.isError && (
                            <p className="text-sm text-rose-400">
                                {listado.error instanceof ErrorApi
                                    ? listado.error.message
                                    : 'No se pudo listar lo que administras'}
                            </p>
                        )}
                        {listado.data?.pedido && listado.data.negocios.length === 0 && (
                            <p className="text-sm text-amber-200/90">
                                Meta no devolvió negocios. En el consentimiento de Facebook hay que
                                marcar los que administras.
                            </p>
                        )}
                        <ul className="space-y-3">
                            {(listado.data?.negocios ?? []).map((n) => (
                                <li
                                    key={n.id}
                                    className="rounded-md border border-neutral-800 bg-neutral-950/60 p-3"
                                >
                                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                                        <h3 className="text-sm font-medium text-neutral-100">
                                            {n.nombre}
                                        </h3>
                                        <p className="text-xs text-neutral-500">
                                            business id {n.id}
                                        </p>
                                    </div>
                                    <p
                                        className={
                                            n.tieneWhatsapp
                                                ? 'mt-1 text-xs text-emerald-300/90'
                                                : 'mt-1 text-xs text-neutral-500'
                                        }
                                    >
                                        {n.tieneWhatsapp ? 'Tiene WhatsApp' : 'Sin WhatsApp'}
                                    </p>
                                    {n.cuentas.length > 0 && (
                                        <ul className="mt-2 space-y-1 text-xs text-neutral-400">
                                            {n.cuentas.map((c) => (
                                                <li key={c.id || c.nombre}>
                                                    WABA {c.nombre || 'sin nombre'} · id {c.id}
                                                    {c.appId ? ` · app ${c.appId}` : ''}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {n.numeros.length > 0 && (
                                        <ul className="mt-2 space-y-1">
                                            {n.numeros.map((num) => (
                                                <li key={num.id || num.numero}>
                                                    <button
                                                        type="button"
                                                        className="text-left text-sm text-sky-300 hover:underline"
                                                        onClick={() =>
                                                            setConectar((prev) => ({
                                                                ...prev,
                                                                phoneNumber: num.numero,
                                                                phoneNumberId: num.id,
                                                                businessAccountId:
                                                                    num.cuentaId ??
                                                                    n.cuentas[0]?.id ??
                                                                    prev.businessAccountId,
                                                            }))
                                                        }
                                                    >
                                                        {num.numero || 'sin número'}
                                                    </button>
                                                    <span className="ml-2 text-xs text-neutral-500">
                                                        phoneNumberId {num.id}
                                                        {num.estado ? ` · ${num.estado}` : ''}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {n.aviso && (
                                        <p className="mt-2 text-xs text-rose-400">{n.aviso}</p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </section>
                    {listado.data?.pedido && !listado.data.mcp.listo && (
                        <p className="text-sm text-neutral-500">
                            Las herramientas de configurar (plantillas, webhooks, envío) se
                            habilitan cuando el MCP esté listo. El botón de arriba es el que
                            pregunta.
                        </p>
                    )}
                    {tools.isError && (
                        <p className="text-sm text-rose-400">
                            {tools.error instanceof ErrorApi
                                ? tools.error.message
                                : 'No se pudieron listar las herramientas del MCP.'}
                        </p>
                    )}
                    {grupos.map((g) => (
                        <section
                            key={g.id}
                            className="space-y-3"
                            aria-label={g.titulo}
                            data-testid={`mcp-grupo-${g.id}`}
                        >
                            <h2 className="text-sm font-semibold text-neutral-200">{g.titulo}</h2>
                            {g.id === 'discovery' && (
                                <p className="text-xs text-neutral-500">
                                    Empieza por {TOOLS_DISCOVERY[0]} para elegir un negocio de Meta.
                                </p>
                            )}
                            {g.tools.map((t) => (
                                <FormHerramienta
                                    key={t.name}
                                    tool={t}
                                    onResultado={setResultado}
                                />
                            ))}
                        </section>
                    ))}
                    {resultado && (
                        <section className="space-y-2" aria-label="Resultado">
                            <h2 className="text-sm font-semibold text-neutral-200">Resultado</h2>
                            <pre className="max-h-80 overflow-auto rounded-md border border-neutral-800 bg-neutral-950 p-3 text-xs text-neutral-300">
                                {resultado}
                            </pre>
                        </section>
                    )}
                </>
            )}

            <section
                className="space-y-3 rounded-lg border border-neutral-800 p-4"
                aria-label="Conectar WhatsApp con Chatwoot"
            >
                <h2 className="text-sm font-semibold text-neutral-200">
                    Conectar WhatsApp con Chatwoot
                </h2>
                <p className="text-xs text-neutral-500">
                    Elige un número ya listado y un negocio Agendify. El provisioner crea o reanuda
                    el inbox (el mismo del alta). Si ya hay canal WhatsApp, se avisa y no se
                    duplica. Sergio y AIonRoot no se pisan. El token permanente se pega aquí (el
                    MCP no lo devuelve en claro) y se guarda cifrado.
                </p>
                <form
                    className="grid gap-2"
                    onSubmit={(e) => {
                        e.preventDefault()
                        atar.mutate()
                    }}
                >
                    <label className="text-xs text-neutral-400">
                        Negocio Agendify
                        <select
                            className="mt-1 w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1 text-sm text-neutral-100"
                            value={conectar.accountId}
                            onChange={(e) =>
                                setConectar((c) => ({ ...c, accountId: e.target.value }))
                            }
                            required
                        >
                            <option value="">Elegir…</option>
                            {(negocios.data?.items ?? []).map((n) => (
                                <option key={n.accountId} value={n.accountId}>
                                    {n.name} (#{n.accountId}
                                    {n.altaId ? ', en espera' : ''})
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="text-xs text-neutral-400">
                        Número
                        <input
                            className="mt-1 w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1 text-sm"
                            value={conectar.phoneNumber}
                            onChange={(e) =>
                                setConectar((c) => ({ ...c, phoneNumber: e.target.value }))
                            }
                            required
                        />
                    </label>
                    <label className="text-xs text-neutral-400">
                        phoneNumberId
                        <input
                            className="mt-1 w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1 text-sm"
                            value={conectar.phoneNumberId}
                            onChange={(e) =>
                                setConectar((c) => ({ ...c, phoneNumberId: e.target.value }))
                            }
                            required
                        />
                    </label>
                    <label className="text-xs text-neutral-400">
                        businessAccountId
                        <input
                            className="mt-1 w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1 text-sm"
                            value={conectar.businessAccountId}
                            onChange={(e) =>
                                setConectar((c) => ({ ...c, businessAccountId: e.target.value }))
                            }
                            required
                        />
                    </label>
                    <label className="text-xs text-neutral-400">
                        Token permanente (apiKey)
                        <input
                            type="password"
                            className="mt-1 w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1 text-sm"
                            value={conectar.apiKey}
                            onChange={(e) =>
                                setConectar((c) => ({ ...c, apiKey: e.target.value }))
                            }
                            required
                        />
                    </label>
                    {avisoConectar && (
                        <p className="text-sm text-amber-200/90" data-testid="mcp-conectar-aviso">
                            {avisoConectar}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={atar.isPending}
                        className="rounded-md bg-sky-600 px-3 py-1.5 text-sm text-white disabled:opacity-40"
                    >
                        {atar.isPending ? 'Conectando…' : 'Conectar'}
                    </button>
                </form>
            </section>
        </div>
    )
}
